import {
  AdminEdmContextEntity,
  AdminEdmContextLiteral,
  AdminEdmLiteral,
} from "../../../types/adminEdm";

export const EDM_LITERAL_PROPERTY_MAP = {
  titles: "dc:title",
  descriptions: "dc:description",
  creators: "dc:creator",
  contributors: "dc:contributor",
  subjects: "dc:subject",
  types: "dc:type",
  materials: "dcterms:medium",
  relatedTo: "edm:isRelatedTo",
} as const;

export type EdmLiteralFieldName = keyof typeof EDM_LITERAL_PROPERTY_MAP;

export const createEmptyEdmLiteral = (property: string, defaultLanguage = "en"): AdminEdmLiteral => ({
  property,
  value: "",
  lang: defaultLanguage,
});

const normalizeFallbackLiteralValues = (fallbackValues: Array<string | null | undefined>): string[] => (
  Array.from(new Set(
    fallbackValues
      .map((value) => (value || "").trim())
      .filter(Boolean)
  ))
);

export const normalizeEdmLiteralListForForm = (
  literals: AdminEdmLiteral[] | null | undefined,
  property: string,
  defaultLanguage = "en",
  fallbackValues: Array<string | null | undefined> = [],
  blankFallbackLanguage = false,
): AdminEdmLiteral[] => (
  Array.isArray(literals) && literals.length
    ? literals.map((literal) => ({
      property,
      value: literal?.value || "",
      lang: literal?.lang || "",
    }))
    : normalizeFallbackLiteralValues(fallbackValues).length
      ? normalizeFallbackLiteralValues(fallbackValues).map((value) => ({
        property,
        value,
        lang: blankFallbackLanguage ? "" : defaultLanguage,
      }))
      : []
);

export const serializeEdmLiteralList = (
  literals: AdminEdmLiteral[] | null | undefined,
  property: string
): AdminEdmLiteral[] => (
  Array.isArray(literals)
    ? literals
      .map((literal) => ({
        property,
        value: (literal?.value || "").trim(),
        lang: (literal?.lang || "").trim() || null,
      }))
      .filter((literal) => literal.value)
    : []
);

export const clearCreatorLinksWithoutExplicitCreators = (
  contexts: AdminEdmContextEntity[] | null | undefined,
  creators: AdminEdmLiteral[] | null | undefined,
): AdminEdmContextEntity[] => {
  const hasExplicitCreators = Array.isArray(creators) && creators.some((literal) => Boolean((literal?.value || "").trim()));

  return Array.isArray(contexts)
    ? contexts.map((context) => (
      !hasExplicitCreators && context.type === "Agent" && context.providedChoLink === "creator"
        ? {
          ...context,
          providedChoLink: null,
        }
        : context
    ))
    : [];
};

export const createEmptyContextEntity = (): AdminEdmContextEntity => ({
  type: "Concept",
  mode: "reference",
  uri: "",
  referenceUri: "",
  providedChoLink: null,
  prefLabel: [{ property: "skos:prefLabel", value: "", lang: "en" }],
  sameAs: "",
  note: "",
  begin: "",
  end: "",
  exactMatch: "",
  broader: "",
  related: "",
  lat: null,
  long: null,
  professionOrOccupation: "",
  dateOfBirth: "",
  dateOfDeath: "",
  placeOfBirth: "",
  placeOfDeath: "",
});

export const normalizeContextEntityForForm = (entity?: AdminEdmContextEntity | null): AdminEdmContextEntity => ({
  ...createEmptyContextEntity(),
  ...entity,
  prefLabel: entity?.prefLabel?.length ? entity.prefLabel : [{ property: "skos:prefLabel", value: "", lang: "en" }],
});

export const getPrimaryContextLabel = (
  context: AdminEdmContextEntity
): AdminEdmContextLiteral => context.prefLabel?.[0] || { property: "skos:prefLabel", value: "", lang: "en" };

export const serializeContextEntities = (contexts: AdminEdmContextEntity[] | null | undefined): AdminEdmContextEntity[] => (
  Array.isArray(contexts)
    ? contexts.map((context) => {
      const primaryLabel = getPrimaryContextLabel(context);
      return {
        ...context,
        uri: context.uri || null,
        referenceUri: context.referenceUri || null,
        providedChoLink: context.type === "Agent" ? (context.providedChoLink || null) : null,
        sameAs: context.sameAs || null,
        note: context.note || null,
        begin: context.begin || null,
        end: context.end || null,
        exactMatch: context.exactMatch || null,
        broader: context.broader || null,
        related: context.related || null,
        professionOrOccupation: context.professionOrOccupation || null,
        dateOfBirth: context.dateOfBirth || null,
        dateOfDeath: context.dateOfDeath || null,
        placeOfBirth: context.placeOfBirth || null,
        placeOfDeath: context.placeOfDeath || null,
        prefLabel: primaryLabel.value ? [{
          property: primaryLabel.property || "skos:prefLabel",
          value: primaryLabel.value,
          lang: primaryLabel.lang || null,
        }] : [],
      };
    })
    : []
);

